/**
 * Les catégories d'objets de la recherche globale : leur libellé, leur icône, et l'écran de détail qui
 * les ouvre DANS CHAQUE ESPACE.
 *
 * <p>La route dépend de l'espace, pas seulement de l'objet : une EEP s'ouvre sur `/controleur/entreprises/…`
 * pour le contrôleur et sur `/presidence/entreprises/…` pour le Président. Un résultat mène donc toujours
 * à un écran de l'espace où l'on se trouve — jamais à un écran dont la garde de rôle éjecterait l'agent.
 *
 * <p>Le serveur décide QUELLES catégories un espace interroge (`ContributeurRecherche.espaces()`) ; ce
 * tableau-ci dit seulement OÙ les ouvrir. Une catégorie servie sans route ici n'est pas affichée : une
 * ligne qui ne mène nulle part est pire qu'absente.
 *
 * WORDING (règle n°1) — libellés repris du menu et du glossaire :
 *  • `المؤسسات والمنشآت العمومية` — entrée du menu Présidence/Reporting ;
 *  • `مذكرات الإعلام` — glossaire §8.7 (CCE 2026-08-27) ;
 *  • `مراقبو الدولة` — intitulé de l'espace (glossaire §2) ;
 *  • `مهمات الرقابة` — pluriel de `مهمة رقابة` (glossaire §4), à faire ratifier ;
 *  • `طلبات المساندة`, `تقرير النشاط الثلاثي`, `التكوين`, `التعاون الدولي` — entrées du menu ;
 *  • `التقرير السنوي لمراقب الدولة`, `تقرير نقل المهام بين مراقبي الدولة`, `مكتب الخبرة في المحاسبة`,
 *    `جلسات اللجنة` — glossaire, verbatim ;
 *  • `جلسات مجلس الهيئة` — composé, à faire ratifier ;
 *  • `ملفات التعيين` — composé du menu CAEEP, à faire ratifier ; `الخبراء المحاسبون` — entrée du menu.
 */
export interface CategorieDef {
  readonly ar: string;
  readonly fr: string;
  readonly icone: string;
  /**
   * Modèle de la route de détail par espace : `:id` = l'identifiant de l'objet, tout autre `:param` est
   * pris dans `parametres` du résultat (ex. `:year` d'un rapport annuel).
   */
  readonly routes: Readonly<Record<string, string>>;
  /**
   * Paramètres de requête de la route, par espace, sur le même modèle (`:id`, `:param`). Pour un écran
   * qui ouvre l'objet par sa requête et non par son chemin — le dossier CAC s'ouvre par
   * `/caeep/nominations?dossier=:id`, le lien profond que l'écran lit déjà. Une chaîne « /x?y=z » dans
   * `routes` viserait un segment « x?y=z » qui n'existe pas.
   */
  readonly requetes?: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

export const CATEGORIES: Readonly<Record<string, CategorieDef>> = {
  EEP: {
    ar: 'المؤسسات والمنشآت العمومية',
    fr: 'Établissements et entreprises publics',
    icone: 'building-2',
    routes: {
      controleur: '/controleur/entreprises/:id',
      presidence: '/presidence/entreprises/:id',
      reporting: '/reporting/entreprises/:id',
      // Décision Q11c (2026-09-29) : la fiche EEP en lecture dans l'espace CAEEP.
      caeep: '/caeep/entreprises/:id',
    },
  },
  MISSION: {
    ar: 'مهمات الرقابة',
    fr: 'Missions de contrôle',
    icone: 'briefcase',
    routes: {
      controleur: '/controleur/missions/:id',
      presidence: '/presidence/missions/:id',
    },
  },
  NOTE_ALERTE: {
    ar: 'مذكرات الإعلام',
    fr: "Notes d'alerte",
    icone: 'bell-ring',
    routes: {
      controleur: '/controleur/notes-alerte/:id',
      presidence: '/presidence/notes-alerte/:id',
    },
  },
  CONTROLEUR: {
    ar: 'مراقبو الدولة',
    fr: "Contrôleurs d'État",
    icone: 'user-check',
    routes: {
      presidence: '/presidence/controleurs/:id',
      cellule: '/cellule/controleurs/:id',
      reporting: '/reporting/controleurs/:id',
    },
  },
  DEMANDE_APPUI: {
    ar: 'طلبات المساندة',
    fr: "Demandes d'appui",
    icone: 'life-buoy',
    routes: {
      controleur: '/controleur/demandes-appui/:id',
      presidence: '/presidence/demandes-appui/:id',
    },
  },
  RAPPORT_ANNUEL: {
    ar: 'التقرير السنوي لمراقب الدولة',
    fr: 'Rapport annuel du contrôleur',
    icone: 'file-text',
    routes: {
      // L'écran du contrôleur porte l'année dans l'URL ; le serveur la sert en paramètre `year`.
      controleur: '/controleur/rapport-annuel/:year/:id',
      presidence: '/presidence/rapports/:id',
    },
  },
  RAPPORT_TRIMESTRIEL: {
    ar: 'تقرير النشاط الثلاثي',
    fr: "Rapport d'activité trimestriel",
    icone: 'file-clock',
    routes: {
      controleur: '/controleur/rapport-trimestriel/:period/:id',
      presidence: '/presidence/rapports-trimestriels/:id',
    },
  },
  PASSATION: {
    ar: 'تقرير نقل المهام بين مراقبي الدولة',
    fr: 'Passations',
    icone: 'arrow-left-right',
    routes: {
      controleur: '/controleur/passations/:id',
      presidence: '/presidence/passations/:id',
    },
  },
  SEANCE_CONSEIL: {
    // [À ratifier] « جلسات » + l'intitulé de l'espace (glossaire §2) ; aucun pluriel au glossaire.
    ar: 'جلسات مجلس هيئة مراقبي الدولة',
    fr: 'Séances du Conseil',
    icone: 'calendar-days',
    routes: { conseil: '/conseil/seances/:id' },
  },
  SEANCE_CAEEP: {
    ar: 'جلسات اللجنة',
    fr: 'Séances de la commission',
    icone: 'calendar-days',
    routes: { caeep: '/caeep/seances/:id' },
  },
  CABINET_CAC: {
    ar: 'مكتب الخبرة في المحاسبة',
    fr: "Cabinets d'expertise comptable",
    icone: 'building',
    routes: { caeep: '/caeep/cabinets/:id' },
  },
  // Revue CAC du 2026-09-29 (TRV-10). [À ratifier] « ملفات التعيين » compose le menu « التدقيق في ملفات
  // تعيين مراقبي الحسابات » ; « الخبراء المحاسبون » est l'entrée du menu.
  DOSSIER_CAC: {
    ar: 'ملفات التعيين',
    fr: 'Dossiers de nomination',
    icone: 'inbox',
    routes: { caeep: '/caeep/nominations' },
    requetes: { caeep: { dossier: ':id' } },
  },
  EXPERT_CAC: {
    ar: 'الخبراء المحاسبون',
    fr: 'Experts-comptables',
    icone: 'user-check',
    routes: { caeep: '/caeep/experts/:id' },
  },
  FORMATION: {
    ar: 'التكوين',
    fr: 'Formation',
    icone: 'graduation-cap',
    routes: { cellule: '/cellule/formations/:id' },
  },
  PROJET_COOPERATION: {
    ar: 'التعاون الدولي',
    fr: 'Coopération internationale',
    icone: 'globe',
    routes: { cellule: '/cellule/cooperation/projets/:id' },
  },
};

/** Les espaces où au moins une catégorie s'ouvre : ailleurs, on n'interroge pas le serveur. */
export const ESPACES_AVEC_DONNEES: ReadonlySet<string> = new Set(
  Object.values(CATEGORIES).flatMap((c) => Object.keys(c.routes)),
);
